import { format } from 'date-fns';
import type {
  EventFormOptions,
  EventPublishState,
  EventStatus,
  RegistrationClosedReason,
  RegistrationMemberType,
} from '../types';
import { MY_PAYMENT_STATUS_LABELS, PAYMENT_STATUS_LABELS } from './payment-status';

export { MY_PAYMENT_STATUS_LABELS, PAYMENT_STATUS_LABELS };

export function defaultEventFormOptions(): EventFormOptions {
  return {
    collectDob: false,
    collectGender: false,
    memberChoice: false,
    paymentRequired: false,
    teamSize: null,
    notesQuestion: null,
    notesRequired: false,
    minAgeYears: null,
    ageAsOn: null,
    paymentInstructions: null,
    whatsappGroupUrl: null,
    whatsappGroupPublic: false,
  };
}

export const PUBLISH_STATE_LABELS: Record<EventPublishState, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

export function registrationStatusLabel(open: boolean, reason: RegistrationClosedReason): string {
  if (open) return 'Registrations open';
  switch (reason) {
    case 'not_required':
      return 'No registration needed';
    case 'coming_soon':
      return 'Registrations opening soon';
    case 'deadline':
      return 'Deadline passed';
    case 'full':
      return 'Full';
    case 'archived':
      return 'Archived';
    default:
      return 'Registrations closed';
  }
}

/** Collection drives and similar events: people take part without signing up. */
export function eventSkipsRegistration(event: {
  registrationNotRequired?: boolean;
  registrationClosedReason?: RegistrationClosedReason | null;
}): boolean {
  return event.registrationNotRequired === true || event.registrationClosedReason === 'not_required';
}

/** Visible event whose signup form is not open yet. */
export function eventRegistrationOpeningSoon(event: {
  registrationComingSoon?: boolean;
  registrationClosedReason?: RegistrationClosedReason | null;
}): boolean {
  return event.registrationComingSoon === true || event.registrationClosedReason === 'coming_soon';
}

/**
 * Admin list copy for the stored "Registrations open" toggle.
 * Effective reasons (draft, deadline, full) are appended so a closed public
 * badge is explained without hiding the toggle itself.
 */
export function adminRegistrationStateLabel(event: {
  registrationToggle?: boolean;
  registrationOpen: boolean;
  registrationNotRequired?: boolean;
  registrationComingSoon?: boolean;
  registrationClosedReason: RegistrationClosedReason;
  publishState: EventPublishState;
}): string {
  const toggleOn = event.registrationToggle ?? event.registrationOpen;
  if (event.publishState === 'archived') return 'Archived';
  if (eventSkipsRegistration(event)) {
    return event.publishState === 'draft'
      ? 'No registration needed · hidden until published'
      : 'No registration needed';
  }
  if (eventRegistrationOpeningSoon(event)) {
    return event.publishState === 'draft'
      ? 'Registrations opening soon · hidden until published'
      : 'Registrations opening soon';
  }
  if (!toggleOn) return 'Registrations closed';
  if (event.publishState === 'draft') return 'Registrations open · hidden until published';
  if (event.registrationClosedReason === 'deadline') return 'Registrations open · deadline passed';
  if (event.registrationClosedReason === 'full') return 'Registrations open · full';
  if (event.registrationOpen) return 'Registrations open';
  return 'Registrations closed';
}

/** Whole years between dob and asOn (both YYYY-MM-DD). */
export function ageOn(dob: string, asOn: string): number {
  const [by, bm, bd] = dob.split('-').map(Number) as [number, number, number];
  const [ay, am, ad] = asOn.split('-').map(Number) as [number, number, number];
  let age = ay - by;
  if (am < bm || (am === bm && ad < bd)) age -= 1;
  return age;
}

export function formatRupees(amount: number | null | undefined): string {
  if (amount == null) return '—';
  return amount === 0 ? 'Free' : `₹${amount.toLocaleString('en-IN')}`;
}

/** Trimmed text, or null when the value is missing or blank. */
export function presentText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/** Date, with location appended only when a location is set. */
export function eventScheduleLine(
  dateIso: string,
  location: string | null | undefined,
  pattern = 'PPP p',
): string {
  const when = format(new Date(dateIso), pattern);
  const where = presentText(location);
  return where ? `${when} · ${where}` : when;
}

/** Mirrors the Worker's fee rule: member fee only when `memberChoice` is on and the registrant is a member. */
export function eventAmountDue(
  event: { feeMember?: number | null; feeNonMember?: number | null },
  options: EventFormOptions,
  memberType: RegistrationMemberType | null,
): number | null {
  const fee =
    options.memberChoice && memberType === 'member' ? event.feeMember ?? null : event.feeNonMember ?? null;
  return fee != null && fee > 0 ? fee : null;
}

/**
 * Events shown on the member /events page: published events, plus archived ones only as past
 * highlights. Drafts are hidden (admins also receive them from the API).
 */
export function memberVisibleEvents<T extends { publishState: EventPublishState; status: EventStatus }>(
  events: T[],
): T[] {
  return events.filter(
    (e) => e.publishState === 'published' || (e.publishState === 'archived' && e.status === 'past'),
  );
}

export const PAST_EVENTS_MONTHS = 12;

export function isPastEventWithinWindow(date: string, now = new Date()): boolean {
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - PAST_EVENTS_MONTHS);
  return new Date(date) >= cutoff;
}

const NOTE_HTTP_URL = /https?:\/\/[^\s<>"']+/;

export type NoteSegment = { type: 'text' | 'link'; value: string };

function isHttpUrl(value: string): boolean {
  return new RegExp(`^${NOTE_HTTP_URL.source}$`).test(value);
}

/** Split registration notes so http(s) proof URLs can be opened by admins. */
export function noteSegments(notes: string | null | undefined): NoteSegment[] {
  if (!notes) return [];
  return notes
    .split(new RegExp(`(${NOTE_HTTP_URL.source})`, 'g'))
    .filter((part) => part.length > 0)
    .map((value) => ({ type: isHttpUrl(value) ? 'link' : 'text', value }));
}

export function firstHttpUrl(notes: string | null | undefined): string | null {
  return noteSegments(notes).find((part) => part.type === 'link')?.value ?? null;
}

/**
 * Notes column for the admin registration list.
 * Only a configured question is shown. Proof URLs stay on the payment Proof control.
 */
export function registrationNotesHeading(
  notesQuestion: string | null | undefined,
  _rowsHaveNotes: boolean,
): string | null {
  return notesQuestion?.trim() || null;
}

export function groupEventsByStatus<T extends { status: EventStatus; date: string }>(
  events: T[],
  now = new Date(),
): { active: T[]; upcoming: T[]; past: T[] } {
  const active = events.filter((e) => e.status === 'active');
  const upcoming = events.filter((e) => e.status === 'upcoming');
  const past = events
    .filter((e) => e.status === 'past' && isPastEventWithinWindow(e.date, now))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  return { active, upcoming, past };
}
